import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { DataTablePFSEvent } from "primereact/datatable";
import { useCallback, useState } from "react";
import { DCSQueryClient } from "../../../queryClient";
import { If } from "../../../components/If/If";
import { useFilterQuery } from "../../../hooks/useFilterQuery";
import { useToast } from "../../../Providers/ToastProvider";
import { requests } from "../../../services/http-common";
import { IFilter } from "../../../types";
import { DeleteItemPopup } from "../components/DeleteItemPopup/DeleteItemPopup";
import { ItemList } from "../components/ItemList/ItemList";
import { SetItemPopup } from "../components/SetItemPopup/SetItemPopup";
import { TItem } from "../components/types";
import classes from "./tabs.module.scss";
import { PrivateAccess } from "../../../keycloak";
import { UserRoles } from "../../../constants/userRoles";
import { useEffectWithoutMount } from "../../../helpers/helperHooks";

const defaultFilters = {
  first: 0,
  rows: 10,
  page: 1,
  filter: "",
  filterByStatus: "",
  filterByCustomerCategory: "",
  sortOrder: -1,
};

export const Supplies = () => {
  const [filters, setFilters] = useState<IFilter>(defaultFilters as IFilter);
  const [item, setItem] = useState<TItem | null>(null);
  const [itemDialog, setItemDialog] = useState(false);
  const [deleteItemDialog, setDeleteItemDialog] = useState(false);

  const filterQuery = useFilterQuery(filters);
  const { show } = useToast();

  const { data, refetch } = useQuery({
    queryKey: ["supplies-oprions"],
    queryFn: () => {
      return requests.get(`/supplies?${filterQuery}`);
    },
  });

  useEffectWithoutMount(() => {
    refetch();
  }, [filters, refetch]);

  const addItem = useMutation({
    mutationFn: (name: string) => {
      return requests.post("/supplies", { name });
    },
    onSuccess: () => {
      hideItemDialog();
      show({ detail: "Item added successfully !" });
      DCSQueryClient.invalidateQueries({
        queryKey: ["supplies-oprions"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const editItemRequest = useMutation({
    mutationFn: ({ name, id }: TItem) => {
      return requests.put(`/supplies/${id}`, { name });
    },
    onSuccess: () => {
      hideItemDialog();
      show({ detail: "Item updated successfully !" });
      DCSQueryClient.invalidateQueries({
        queryKey: ["supplies-oprions"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const deleteItem = useMutation({
    mutationFn: ({ id }: TItem) => {
      return requests.delete(`/supplies/${id}`);
    },
    onSuccess: () => {
      setDeleteItemDialog(false);
      DCSQueryClient.invalidateQueries({
        queryKey: ["supplies-oprions"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const handleSetItem = (item: { name: string; id: string | null }) => {
    if (item.id) {
      editItemRequest.mutate(item as TItem);
      return;
    }
    addItem.mutate(item.name);
  };

  const onPage = useCallback(
    (event: DataTablePFSEvent) => {
      setFilters((currVal: any) => ({
        ...currVal,
        first: event.first,
        rows: event.rows,
        page: event.page,
      }));
    },
    [setFilters]
  );

  const editItem = (item: TItem) => {
    setItem(item);
    setItemDialog(true);
  };

  const confirmDeleteItem = (item: TItem) => {
    setItem(item);
    setDeleteItemDialog(true);
  };

  const hideDeleteItemDialog = () => {
    setDeleteItemDialog(false);
  };

  const hideItemDialog = () => {
    setItemDialog(false);
    setItem(null);
  };

  const handleAddItem = () => {
    setItem(null);
    setItemDialog(true);
  };

  const cardTitle = () => {
    return (
      <div className={classes.cardTitle}>
        <h4>Supplying Products</h4>
        <PrivateAccess
          roles={{
            dcs_client: [UserRoles.SUPPLIES_CREATE],
          }}
        >
          <Button
            icon="pi pi-plus"
            className=""
            label="New"
            onClick={handleAddItem}
          />
        </PrivateAccess>
      </div>
    );
  };

  return (
    <div className={classes.container}>
      <Card title={cardTitle}>
        <ItemList
          totalRecords={data?.total}
          records={data?.result}
          first={filters.first}
          rows={filters.rows}
          onPage={onPage}
          rowActionDelete={confirmDeleteItem}
          rowActionEdit={editItem}
        />
      </Card>
      <If condition={deleteItemDialog}>
        <DeleteItemPopup
          item={item}
          onHide={hideDeleteItemDialog}
          onConfirm={deleteItem.mutate}
          deleteRequestPending={deleteItem.isLoading}
        />
      </If>
      <If condition={itemDialog}>
        <SetItemPopup
          item={item}
          onHide={hideItemDialog}
          onSubmit={handleSetItem}
        />
      </If>
    </div>
  );
};
